import type { Action, ModuleId, Subject } from '@prody/shared';
import type {
  Command,
  DashboardWidget,
  ModuleDefinition,
  NavEntry,
  NavGroup,
  SearchHit,
} from './types';

/* ------------------------------------------------------------------ *
 * The registry is a pure function of (all modules, this tenant's
 * enabled list, this user's grants). It is computed once per business
 * and read everywhere.
 * ------------------------------------------------------------------ */

export const NAV_GROUP_ORDER: readonly NavGroup[] = [
  'trade',   // orders, market, catalog
  'move',    // purchasing, logistics, stock
  'people',  // contacts, messaging, schedule
  'money',   // invoices, payments, analytics
  'manage',  // team, settings
];

export interface ResolvedRegistry {
  modules: ModuleDefinition[];
  /** Nav entries grouped and sorted, already filtered by grant and tenant. */
  nav: { group: NavGroup; entries: (NavEntry & { moduleId: ModuleId })[] }[];
  commands: Command[];
  widgets: DashboardWidget[];
  searchProviders: NonNullable<ModuleDefinition['search']>[];
  has: (id: ModuleId) => boolean;
}

export interface RegistryInput {
  all: ModuleDefinition[];
  enabledModules: readonly ModuleId[];
  can: (subject: Subject, action: Action) => boolean;
}

export const resolveRegistry = ({ all, enabledModules, can }: RegistryInput): ResolvedRegistry => {
  const enabled = new Set(enabledModules);

  const modules = all.filter(
    (m) => enabled.has(m.id) && can(m.requires.subject, m.requires.action),
  );

  const byGroup = new Map<NavGroup, (NavEntry & { moduleId: ModuleId })[]>();
  for (const m of modules) {
    if (!m.nav) continue;
    const list = byGroup.get(m.nav.group) ?? [];
    list.push({ ...m.nav, moduleId: m.id });
    byGroup.set(m.nav.group, list);
  }

  const nav = NAV_GROUP_ORDER.filter((g) => byGroup.has(g)).map((group) => ({
    group,
    entries: (byGroup.get(group) ?? []).sort((a, b) => a.order - b.order),
  }));

  const allowed = <T extends { requires?: { subject: Subject; action: Action } }>(item: T): boolean =>
    !item.requires || can(item.requires.subject, item.requires.action);

  return {
    modules,
    nav,
    commands: modules.flatMap((m) => m.commands ?? []).filter(allowed),
    widgets: modules
      .flatMap((m) => m.widgets ?? [])
      .filter(allowed)
      .sort((a, b) => a.order - b.order),
    searchProviders: modules.map((m) => m.search).filter((s): s is NonNullable<typeof s> => Boolean(s)),
    has: (id) => modules.some((m) => m.id === id),
  };
};

/** Runs every module's search provider in parallel, tolerating failures. */
export const runSearch = async (
  registry: ResolvedRegistry,
  query: string,
  businessId: string,
  businessSlug: string,
): Promise<SearchHit[]> => {
  if (query.trim().length < 2) return [];
  const results = await Promise.allSettled(
    registry.searchProviders.map((provider) => provider(query, { businessId, businessSlug })),
  );
  return results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
};
